import { useState, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Switch } from "@/components/ui/switch";
import { Ghost, LogOut, Eye, EyeOff, Download, Bell, BellOff, Share, Smartphone, Flame, AlertTriangle, Volume2 } from "lucide-react";
import { usePWAInstall } from "@/hooks/usePWAInstall";
import { getNotificationsEnabled, setNotificationsPref, requestNotificationPermission, subscribeToPush, unsubscribeFromPush, SOUND_PRESETS, getUrgentSound, setUrgentSound, playSound } from "@/lib/notifications";

export default function SettingsTab() {
  const { user, setUser, logout } = useAuth();
  const qc = useQueryClient();
  const { canInstall, installed, promptInstall, isIOS } = usePWAInstall();
  const [notifOn, setNotifOn] = useState(getNotificationsEnabled());
  const [urgentSound, setUrgentSoundState] = useState(getUrgentSound());
  const statusFileRef = useRef(null);

  const toggleGhost = useMutation({
    mutationFn: (val) => api.post("/settings/ghost", { ghost_mode: val }),
    onSuccess: (res) => {
      setUser(res.data);
      toast.success(res.data.ghost_mode ? "Mode fantôme activé" : "Mode fantôme désactivé");
      qc.invalidateQueries();
    },
  });

  const toggleHeat = useMutation({
    mutationFn: (val) => api.post("/settings/heat", { in_heat: val }),
    onSuccess: (res) => {
      setUser(res.data);
      toast.success(res.data.in_heat ? "🦁 Vous êtes en chaleur !" : "Statut « en chaleur » désactivé");
      qc.invalidateQueries();
    },
  });

  const uploadStatus = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    toast.loading("Publication du statut…", { id: "status" });
    try {
      await api.post("/status", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("🔥 Statut publié pour 24h", { id: "status" });
      qc.invalidateQueries({ queryKey: ["statuses"] });
    } catch {
      toast.error("Échec de la publication", { id: "status" });
    }
    e.target.value = "";
  };

  const handleInstall = async () => {
    if (canInstall) {
      const ok = await promptInstall();
      if (ok) toast.success("Wado est en cours d'installation");
    } else if (isIOS) {
      toast.info("Sur iPhone : appuyez sur Partager puis « Sur l'écran d'accueil »", { duration: 6000 });
    } else {
      toast.info("Ouvrez le menu du navigateur puis « Installer l'application »", { duration: 6000 });
    }
  };

  const toggleNotifications = async (val) => {
    if (val) {
      const perm = await requestNotificationPermission();
      if (perm === "granted") {
        setNotificationsPref(true);
        setNotifOn(true);
        subscribeToPush();
        toast.success("Notifications activées");
      } else if (perm === "unsupported") {
        toast.error("Notifications non supportées sur cet appareil");
      } else {
        setNotificationsPref(false);
        setNotifOn(false);
        toast.error("Autorisation refusée. Activez les notifications dans les réglages du navigateur.");
      }
    } else {
      setNotificationsPref(false);
      setNotifOn(false);
      unsubscribeFromPush();
      toast.success("Notifications désactivées");
    }
  };

  return (
    <div>
      <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#8A8A8A] mb-6">Réglages</p>

      <div className="relative overflow-hidden bg-[#121212] border border-white/5 rounded-3xl p-6 mb-6">
        <img
          src="https://images.unsplash.com/photo-1608501821300-4f99e58bba77?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwzfHxkYXJrJTIwYWJzdHJhY3QlMjB0ZXh0dXJlJTIwbHV4dXJ5fGVufDB8fHx8MTc4MjE3NDI0NHww&ixlib=rb-4.1.0&q=85"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-20"
        />
        <div className="relative flex items-center gap-4">
          <Avatar className="h-16 w-16 border border-white/10">
            <AvatarImage src={user?.picture} />
            <AvatarFallback className="bg-[#1A1A1A] text-[#C4A47C] text-xl">{user?.name?.[0]?.toUpperCase()}</AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <p className="font-display text-2xl font-medium tracking-tight truncate">{user?.name}</p>
            <p className="text-sm text-[#8A8A8A] truncate">{user?.email}</p>
          </div>
        </div>
      </div>

      <div className="bg-[#121212] border border-white/5 rounded-3xl p-6 mb-6">
        <div className="flex items-start gap-4">
          <div className={`h-11 w-11 rounded-xl border flex items-center justify-center shrink-0 ${user?.ghost_mode ? "border-[#A3B8CC]/40 ghost-glow" : "border-white/10"}`}>
            <Ghost className={`h-5 w-5 ${user?.ghost_mode ? "text-[#A3B8CC]" : "text-[#8A8A8A]"}`} strokeWidth={1.5} />
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="font-medium">Mode fantôme</p>
                <p className="text-sm text-[#8A8A8A] leading-relaxed mt-1">
                  Lisez les messages sans envoyer d'accusé de lecture. Personne ne saura que vous avez vu ses messages.
                </p>
              </div>
              <Switch
                data-testid="settings-ghost-mode-toggle"
                checked={!!user?.ghost_mode}
                onCheckedChange={(val) => toggleGhost.mutate(val)}
              />
            </div>
            <div className="flex items-center gap-2 mt-4 text-xs text-[#8A8A8A]">
              {user?.ghost_mode ? (
                <><EyeOff className="h-4 w-4 text-[#A3B8CC]" /> <span className="text-[#A3B8CC]">Invisible — vos lectures sont masquées</span></>
              ) : (
                <><Eye className="h-4 w-4" /> <span>Visible — les accusés de lecture sont actifs</span></>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="bg-[#121212] border border-white/5 rounded-3xl p-6 mb-6">
        <div className="flex items-start gap-4">
          <div className={`h-11 w-11 rounded-xl border flex items-center justify-center shrink-0 ${notifOn ? "border-[#C4A47C]/40" : "border-white/10"}`}>
            {notifOn ? <Bell className="h-5 w-5 text-[#C4A47C]" strokeWidth={1.5} /> : <BellOff className="h-5 w-5 text-[#8A8A8A]" strokeWidth={1.5} />}
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="font-medium">Notifications de messages</p>
                <p className="text-sm text-[#8A8A8A] leading-relaxed mt-1">
                  Recevez une alerte lorsqu'un ami vous envoie un nouveau message, même lorsque Wado est en arrière-plan.
                </p>
              </div>
              <Switch
                data-testid="settings-notifications-toggle"
                checked={notifOn}
                onCheckedChange={toggleNotifications}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="relative overflow-hidden bg-[#121212] border border-white/5 rounded-3xl p-6 mb-6">
        <div className="flex items-start gap-4">
          <div className="h-11 w-11 rounded-xl border border-white/10 flex items-center justify-center shrink-0">
            <Smartphone className="h-5 w-5 text-[#A3B8CC]" strokeWidth={1.5} />
          </div>
          <div className="flex-1">
            <p className="font-medium">Installer l'application</p>
            <p className="text-sm text-[#8A8A8A] leading-relaxed mt-1 mb-4">
              {installed
                ? "Wado est installé sur votre appareil. 🎉"
                : "Ajoutez Wado à votre écran d'accueil pour un accès instantané, plein écran, comme une vraie app."}
            </p>
            {!installed && (
              <button
                data-testid="install-pwa-button"
                onClick={handleInstall}
                className="bg-white text-black font-medium px-6 py-3 rounded-full hover:bg-gray-200 transition-colors flex items-center gap-2 hover:-translate-y-[1px] duration-300"
              >
                {isIOS && !canInstall ? <Share className="h-4 w-4" /> : <Download className="h-4 w-4" />}
                {isIOS && !canInstall ? "Ajouter à l'écran d'accueil" : "Installer Wado"}
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="bg-[#121212] border border-white/5 rounded-3xl p-6 mb-6">
        <div className="flex items-start gap-4">
          <div className="h-11 w-11 rounded-xl border border-orange-500/30 flex items-center justify-center shrink-0">
            <Flame className="h-5 w-5 text-orange-400" strokeWidth={1.5} />
          </div>
          <div className="flex-1">
            <p className="font-medium">Flamme — Statut 24h</p>
            <p className="text-sm text-[#8A8A8A] leading-relaxed mt-1 mb-4">
              Publiez une photo ou une courte vidéo en statut visible par vos amis pendant 24 heures, puis elle disparaît automatiquement.
            </p>
            <input ref={statusFileRef} type="file" accept="image/*,video/*" className="hidden" onChange={uploadStatus} data-testid="status-file-input" />
            <button
              data-testid="add-status-button"
              onClick={() => statusFileRef.current?.click()}
              className="bg-white text-black font-medium px-6 py-3 rounded-full hover:bg-gray-200 transition-colors flex items-center gap-2 hover:-translate-y-[1px] duration-300"
            >
              <Flame className="h-4 w-4" /> Publier un statut
            </button>
          </div>
        </div>
      </div>

      <div className="bg-[#121212] border border-white/5 rounded-3xl p-6 mb-6">
        <div className="flex items-start gap-4">
          <div className={`h-11 w-11 rounded-xl border flex items-center justify-center shrink-0 text-xl ${user?.in_heat ? "border-orange-500/40 bg-orange-500/10" : "border-white/10"}`}>
            🦁
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="font-medium">Lion — En chaleur</p>
                <p className="text-sm text-[#8A8A8A] leading-relaxed mt-1">
                  Affichez le statut « en chaleur » 🦁 auprès de vos amis pour signaler que vous êtes chaud·e.
                </p>
              </div>
              <Switch
                data-testid="settings-heat-toggle"
                checked={!!user?.in_heat}
                onCheckedChange={(val) => toggleHeat.mutate(val)}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="bg-[#121212] border border-white/5 rounded-3xl p-6 mb-6">
        <div className="flex items-start gap-4">
          <div className="h-11 w-11 rounded-xl border border-red-500/30 flex items-center justify-center shrink-0">
            <AlertTriangle className="h-5 w-5 text-red-400" strokeWidth={1.5} />
          </div>
          <div className="flex-1">
            <p className="font-medium">Sonnerie des messages urgents</p>
            <p className="text-sm text-[#8A8A8A] leading-relaxed mt-1 mb-4">
              Choisissez le son joué lorsqu'un ami vous envoie un message marqué urgent ⚠️.
            </p>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(SOUND_PRESETS).map(([key, preset]) => (
                <button
                  key={key}
                  data-testid={`urgent-sound-${key}`}
                  onClick={() => { setUrgentSound(key); setUrgentSoundState(key); playSound(key); }}
                  className={`flex items-center justify-between rounded-xl border px-4 py-3 text-sm transition ${urgentSound === key ? "border-red-500/50 bg-red-500/10 text-white" : "border-white/10 text-[#8A8A8A] hover:bg-[#1A1A1A]"}`}
                >
                  <span>{preset.label}</span>
                  {urgentSound === key && <Volume2 className="h-4 w-4 text-red-400" />}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <button
        data-testid="logout-button"
        onClick={logout}
        className="w-full bg-[#121212] border border-white/10 text-[#8A8A8A] hover:text-white hover:bg-[#1A1A1A] px-8 py-4 rounded-full transition flex items-center justify-center gap-2"
      >
        <LogOut className="h-4 w-4" /> Se déconnecter
      </button>
    </div>
  );
}
